import type { Product, UnitDef } from './types';

/**
 * The shop's own vocabulary: the units it sells by and the categories it files
 * products under.
 *
 * These were two fixed arrays in two different files — `UNIT_OPTIONS` in the web
 * app's presets and a `CATEGORY_OPTIONS` local to the add-product screen, which
 * had already drifted out of sync with `CATEGORIES` here. They are seeds now,
 * and the live lists are shop settings.
 */

/** Bulk units are sold in halves, so a half-ton or half-cube load can be rung up. */
export const DEFAULT_UNITS: UnitDef[] = [
  { name: 'ຖົງ', step: 1 },
  { name: 'ເສັ້ນ', step: 1 },
  { name: 'ຄິວ', step: 0.5 },
  { name: 'ໂຕນ', step: 0.5 },
  { name: 'ອັນ', step: 1 },
  { name: 'ກິໂລ', step: 1 },
];

/** ທັງໝົດ is a filter, not a category, so it is deliberately not here. */
export const DEFAULT_CATEGORIES: string[] = ['ຊີມັງ', 'ເຫຼັກ', 'ຊາຍ/ຫີນ', 'ທໍ່', 'ອື່ນໆ'];

export const DEFAULT_EXPENSE_CATEGORIES: string[] = [
  'ຄ່າຂົນສົ່ງ',
  'ຄ່າໄຟ',
  'ຄ່ານ້ຳ',
  'ຄ່າແຮງງານ',
  'ຄ່ານ້ຳມັນ',
  'ຄ່າເຊົ່າ',
  'ອື່ນໆ',
];

/** Case- and space-insensitive, so "ຄ່າ ໄຟ" does not become a second ຄ່າໄຟ. */
function key(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

export function hasValue(list: string[], value: string): boolean {
  const k = key(value);
  return list.some((v) => key(v) === k);
}

/** Appends unless it is already there. Returns the same array when unchanged. */
export function addValue(list: string[], value: string): string[] {
  const trimmed = value.trim();
  if (!trimmed || hasValue(list, trimmed)) return list;
  return [...list, trimmed];
}

export function addUnit(list: UnitDef[], unit: UnitDef): UnitDef[] {
  if (list.some((u) => key(u.name) === key(unit.name))) return list;
  return [...list, { ...unit, name: unit.name.trim() }];
}

export function unitStep(list: UnitDef[], name: string): number {
  return list.find((u) => key(u.name) === key(name))?.step ?? 1;
}

/**
 * Merges the seed categories with every category the saved catalog actually
 * uses.
 *
 * Needed by the migration: an existing device may hold products filed under a
 * category that never appeared in either fixed list, and dropping it would make
 * those products unfilterable on the stock and sell screens.
 */
export function mergeCategories(seed: string[], catalog: Product[] | undefined): string[] {
  let out = [...seed];
  for (const product of catalog ?? []) out = addValue(out, product.cat);
  return out;
}

/**
 * Whether a unit or category can still be renamed or removed.
 *
 * Only unused ones can: a unit in a cart line or a journal entry, or a category
 * on a saved product, would be orphaned by the change, and the ledger must stay
 * readable. Adding is always allowed.
 */
export function categoryInUse(catalog: Product[], cat: string): boolean {
  return catalog.some((p) => key(p.cat) === key(cat));
}

export function unitInUse(catalog: Product[], unit: string): boolean {
  return catalog.some((p) => p.units.some((u) => key(u.unit) === key(unit)));
}
